/**
 * Move a lead to another stage, with a comment that is not optional.
 *
 * Every stage move is a thing that happened — a call that went somewhere, a
 * demo that landed, a quote that went out — and the board has never kept any
 * record of what. So this window asks "what happened?" and will not confirm
 * without it: the comment is required here and, because a required field in one
 * client is not a rule, on the server too (POST /deals/:id/stage with
 * `require`). The minimum length is configurable (settings.stageCommentMin,
 * default 10) and shown as a live counter so the bar to clear is visible rather
 * than guessed at.
 *
 * Sub-statuses (the brief's "status inside a stage") arrive with the new stage
 * model; until then this moves between the stages the workspace already has.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet, { Group, Field } from '../Sheet.jsx';
import {
  companyName, forwardStages, subStatusesOf, displayStage, subStatus
} from '../../domain/crm.js';

export default function MoveStage({ id, onClose }) {
  const { S, crm, moveStage } = useApp();
  const deal = crm.deals.find(d => d.id === id);

  /* The four forward stages the dialog can set (brief §6). Closed is reached by
     winning, losing or turning a lead away — each its own action — not from
     here, so conversion is never driven out of this dialog. */
  const stages = forwardStages(S);
  const min = Number(S.stageCommentMin) > 0 ? Number(S.stageCommentMin) : 10;

  const cur = deal ? displayStage(deal) : 'New';
  /* Default to the next stage along, so the common case is one click plus the
     comment. From a closed stage (or the last one) it falls back to the current. */
  const curIndex = stages.indexOf(cur);
  const defaultStage = curIndex >= 0 && curIndex < stages.length - 1
    ? stages[curIndex + 1] : (stages.includes(cur) ? cur : stages[0]);

  const [stage, setStage] = useState(defaultStage);
  const [sub, setSub] = useState(subStatusesOf(S, defaultStage)[0] || '');
  const [comment, setComment] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!deal) return null;

  const subs = subStatusesOf(S, stage);
  const pickStage = s => { setStage(s); setSub(subStatusesOf(S, s)[0] || ''); };

  const len = comment.trim().length;
  const ready = stage && len >= min;

  const save = async () => {
    if (!ready) return;
    setBusy(true);
    setError('');
    try {
      await moveStage(id, {
        stage,
        subStatus: sub,
        comment: comment.trim(),
        next: next.trim() || undefined
      });
      onClose();
    } catch (e) {
      /* The server is the backstop: if it refuses the comment, say so and keep
         the window open with what was typed. */
      setError((e && e.message) || 'Could not move the stage. Try again.');
      setBusy(false);
    }
  };

  return (
    <Sheet
      icon="flow"
      tone="g-purple"
      title="Move stage"
      onClose={onClose}
      onSave={save}
      saveLabel={busy ? 'Moving…' : 'Move stage'}
      saveDisabled={!ready || busy}
      bodyClass="ms-form"
      hero={(
        <div className="ms-hero">
          <b>{deal.title}</b>
          <span>
            {companyName(crm, deal.cid) || 'no company'}
            {' · now in '}<em>{cur}{subStatus(deal) ? ` · ${subStatus(deal)}` : ''}</em>
          </span>
        </div>
      )}
    >
      <Group>Move to</Group>
      <div className="ms-stages">
        {stages.map(s => (
          <button
            key={s}
            type="button"
            className={`ms-chip${stage === s ? ' on' : ''}`}
            onClick={() => pickStage(s)}
          >
            {stage === s ? <Icon name="check" /> : null}{s}
          </button>
        ))}
      </div>

      {subs.length ? (
        <>
          <Group>Status inside {stage}</Group>
          <div className="ms-stages">
            {subs.map(x => (
              <button
                key={x}
                type="button"
                className={`ms-chip sub${sub === x ? ' on' : ''}`}
                onClick={() => setSub(x)}
              >
                {sub === x ? <Icon name="check" /> : null}{x}
              </button>
            ))}
          </div>
        </>
      ) : null}

      <Field label="What happened?" wide>
        <textarea
          rows={4}
          autoFocus
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Called and booked a demo for Thursday 3pm. Keen on Payroll + Inventory."
        />
        <small className={`ms-count${len > 0 && len < min ? ' bad' : ''}`}>
          {len < min
            ? `${min - len} more character${min - len === 1 ? '' : 's'} — say what happened.`
            : `${len} characters`}
        </small>
      </Field>

      <Field label="Next step (optional)" wide>
        <input
          type="text"
          value={next}
          onChange={e => setNext(e.target.value)}
          placeholder="Send the proposal by Friday"
        />
      </Field>

      {error ? <div className="ms-error">{error}</div> : null}
    </Sheet>
  );
}
